import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Clock, CheckCircle, XCircle, Truck, Eye } from 'lucide-react';
import axios from 'axios';

interface OrderItem {
  productId: string;
  name: string;
  price: number;
  quantity: number;
}

interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
}

interface Order {
  _id: string;
  items: OrderItem[];
  customerInfo: CustomerInfo;
  totalAmount: number;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'failed';
  createdAt: string;
  updatedAt: string;
  paymentIntentId?: string;
  trackingNumber?: string;
}

const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [searchEmail, setSearchEmail] = useState('');

  useEffect(() => {
    // Try to get email from localStorage if available
    const savedEmail = localStorage.getItem('customerEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      fetchOrders(savedEmail);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchOrders = async (customerEmail: string) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/orders/customer/${encodeURIComponent(customerEmail)}`);
      setOrders(response.data);
      localStorage.setItem('customerEmail', customerEmail);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setOrders([]);
      } else {
        setError('حدث خطأ في جلب الطلبات');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchEmail.trim()) {
      fetchOrders(searchEmail.trim());
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'processing':
        return <Package className="h-5 w-5 text-blue-500" />;
      case 'shipped':
        return <Truck className="h-5 w-5 text-purple-500" />;
      case 'delivered':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'cancelled':
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Package className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'في الانتظار';
      case 'processing':
        return 'قيد المعالجة';
      case 'shipped':
        return 'تم الشحن';
      case 'delivered':
        return 'تم التوصيل';
      case 'cancelled':
        return 'ملغي';
      case 'failed':
        return 'فشل';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'processing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'shipped':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      case 'delivered':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'cancelled':
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-dark-bg/20 dark:text-dark-textSecondary';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg min-h-screen transition-colors duration-300">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-8"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-gray-200 dark:bg-gray-700 rounded-lg h-32"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!email && orders.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg min-h-screen transition-colors duration-300">
        <div className="text-center">
          <Package className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-600 mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text mb-4">تتبع طلباتك</h1>
          <p className="text-lg text-gray-600 dark:text-dark-textSecondary mb-8">
            أدخل بريدك الإلكتروني لعرض تاريخ طلباتك
          </p>
          
          <form onSubmit={handleSearch} className="max-w-md mx-auto">
            <div className="flex space-x-2">
              <input
                type="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="أدخل بريدك الإلكتروني"
                required
                className="input-field flex-1"
              />
              <button type="submit" className="btn-primary">
                بحث
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg min-h-screen transition-colors duration-300">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">طلباتي</h1>
        <p className="text-gray-600 dark:text-dark-textSecondary mt-2">
          {email && `البريد الإلكتروني: ${email}`}
        </p>
      </div>

      {email && (
        <div className="mb-6">
          <form onSubmit={handleSearch} className="flex space-x-2 max-w-md">
            <input
              type="email"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              placeholder="البحث عن طريق بريد إلكتروني آخر"
              className="input-field flex-1"
            />
            <button type="submit" className="btn-secondary">
              بحث
            </button>
          </form>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="text-center py-12">
          <Package className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-600 mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-4">لا توجد طلبات</h2>
          <p className="text-gray-600 dark:text-dark-textSecondary mb-8">
            لم يتم العثور على أي طلبات لهذا البريد الإلكتروني
          </p>
          <Link to="/" className="btn-primary">
            العودة للتسوق
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <div
              key={order._id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Order Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(order.status)}
                        <span>{getStatusText(order.status)}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-dark-textSecondary">رقم الطلب</p>
                      <p className="font-mono text-lg font-bold text-gray-900 dark:text-dark-text">#{order._id.slice(-8)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600 dark:text-dark-textSecondary">تاريخ الطلب</p>
                    <p className="font-medium text-gray-900 dark:text-dark-text">
                      {new Date(order.createdAt).toLocaleDateString('en-GB')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="p-6">
                <div className="space-y-4 mb-6">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-dark-text">{item.name}</h4>
                        <p className="text-sm text-gray-600 dark:text-dark-textSecondary">
                          الكمية: {item.quantity} × ₪{item.price}
                        </p>
                      </div>
                      <span className="font-semibold text-gray-900 dark:text-dark-text">
                        ₪{(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Order Summary */}
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900 dark:text-dark-text">المجموع</span>
                    <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                      ₪{order.totalAmount.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Order Details */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-dark-text mb-3">معلومات العميل</h4>
                    <div className="space-y-2 text-sm text-gray-600 dark:text-dark-textSecondary">
                      <p>{order.customerInfo.firstName} {order.customerInfo.lastName}</p>
                      <p>{order.customerInfo.email}</p>
                      <p>{order.customerInfo.phone}</p>
                      <p>{order.customerInfo.address}</p>
                      <p>{order.customerInfo.city}, {order.customerInfo.postalCode}</p>
                      <p>{order.customerInfo.country}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-dark-text mb-3">تفاصيل الطلب</h4>
                    <div className="space-y-2 text-sm text-gray-600 dark:text-dark-textSecondary">
                      <p>الحالة: {getStatusText(order.status)}</p>
                      <p>تاريخ الطلب: {new Date(order.createdAt).toLocaleDateString('en-GB')}</p>
                      <p>آخر تحديث: {new Date(order.updatedAt).toLocaleDateString('en-GB')}</p>
                      {order.trackingNumber && (
                        <p>رقم التتبع: {order.trackingNumber}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex space-x-3">
                  <Link
                    to={`/order/${order._id}`}
                    className="btn-secondary inline-flex items-center space-x-2"
                  >
                    <Eye className="h-4 w-4" />
                    <span>عرض التفاصيل</span>
                  </Link>
                  
                  {order.status === 'delivered' && (
                    <button className="btn-primary">
                      تقييم المنتجات
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
